import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArthaLogo, SunflowerGlyph } from "@/components/ArthaLogo";
import { DemoCTA } from "@/components/DemoCTA";
import { toast } from "sonner";

const Login = () => {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-surface-app text-ink-body">
      <div className="p-8 md:p-16 flex flex-col">
        <Link to="/" data-testid="auth-logo-link"><ArthaLogo size={28} /></Link>
        <div className="my-auto max-w-md w-full">
          <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-6">Sign in</div>
          <h1 className="h-display text-5xl md:text-6xl">Welcome <span className="italic text-artha-amber">back</span>.</h1>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <Field label="Email" type="email" value={email} onChange={setEmail} id="email" />
            <Field label="Password" type="password" value={password} onChange={setPassword} id="password" />
            <button type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-artha-ink text-artha-cream py-4 text-sm tracking-tight hover:bg-artha-amber hover:text-ink-body transition-colors disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="mt-6 text-sm text-ink-muted">
            New here? <Link to="/signup" className="amber-link" data-testid="auth-signup-link">Create an account →</Link>
          </p>
          <div className="mt-6 pt-6 border-t border-border-subtle">
            <div className="text-[10px] uppercase tracking-[0.22em] text-ink-muted font-mono-accent mb-3">Or explore first</div>
            <DemoCTA variant="secondary" label="Launch demo sandbox" testId="login-demo-cta"
              className="w-full justify-center" />
          </div>
        </div>
      </div>
      <div className="hidden lg:flex bg-artha-ink relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 text-center">
          <SunflowerGlyph size={280} />
          <div className="mt-10 text-artha-cream/60 font-serif-display text-2xl italic max-w-xs">
            "Artha" — the Sanskrit word for <span className="text-artha-amber">purpose, means, wealth</span>.
          </div>
        </div>
      </div>
    </div>
  );
};

export const Field = ({ label, type = "text", value, onChange, id }) => (
  <div>
    <label htmlFor={id} className="block text-xs uppercase tracking-[0.2em] text-ink-muted font-mono-accent mb-2">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`input-${id}`}
      required
      className="w-full bg-transparent border border-border-subtle px-4 py-3.5 text-sm focus:outline-none focus:border-artha-ink transition-colors"
    />
  </div>
);

export default Login;
